// Shared container_type enum (Mig 120, split in 175) used wherever bins
// are issued or returned. Pulled out of BinInventory so the deliveries
// page can reuse the same labels when issuing empties at the gate.

export type ContainerType =
  | 'KF49_BIN'      // KF49-OWNED plastic stack bin (invoiced) — NOT a generic
                    // standard; only offered to a tenant that owns a KF49-bin
                    // type row (see standardTypesForTenant).
  | 'CHEP_BIN'      // CHEP rental pool bin (not invoiced — packhouse pays CHEP)
  | 'RED_ARM_LUG'
  | 'GREY_ARM_LUG'
  | 'STEEL_CAGE'
  | 'BLUEBERRY_CRATE'  // berry intake crate (blueberry flow — mig 806)
  | 'STRAWBERRY_CRATE' // berry intake crate (blueberry flow — mig 806)
  | 'PINK_LUG'      // Fresca's pink lug (mig 20261341000000) — like KF49_BIN,
                    // offered only to a tenant with its own PINK_LUG type row.
  | 'CHEP_KF49_BIN' // legacy combined value, kept for type-safety on read paths

// Canonical display order for the general (non-berry) container codes. KF49_BIN
// is included so a KF49-owning tenant shows it in a sensible slot, but it is NOT
// a generic standard — standardTypesForTenant only surfaces it to a tenant that
// owns a KF49-bin type row. The generic set (offered to any packhouse) is simply
// this order minus KF49_BIN; labels come from CONTAINER_LABEL.
const GENERAL_CONTAINER_ORDER: ContainerType[] = [
  'KF49_BIN', 'CHEP_BIN', 'RED_ARM_LUG', 'GREY_ARM_LUG', 'STEEL_CAGE', 'PINK_LUG',
]

// Standard codes only one business brought: never in the unseeded fallback,
// offered only where the tenant holds the code's own type row.
const OWN_TENANT_CODES = new Set<ContainerType>(['KF49_BIN', 'PINK_LUG'])

// Berry intake crate types — used only by the blueberry force-cool flow
// (Precool), NOT the generic citrus container picker above.
export const CRATE_TYPES: { value: ContainerType; label: string }[] = [
  { value: 'BLUEBERRY_CRATE',  label: 'Blueberry crate'  },
  { value: 'STRAWBERRY_CRATE', label: 'Strawberry crate' },
]

const BERRY_CRATE_CODES = new Set<ContainerType>(['BLUEBERRY_CRATE', 'STRAWBERRY_CRATE'])

export const CONTAINER_LABEL: Record<ContainerType, string> = {
  KF49_BIN:         'KF49 bin',
  CHEP_BIN:         'CHEP bin',
  RED_ARM_LUG:      'Red arm-lug',
  GREY_ARM_LUG:     'Grey arm-lug',
  STEEL_CAGE:       'Steel cage',
  BLUEBERRY_CRATE:  'Blueberry crate',
  STRAWBERRY_CRATE: 'Strawberry crate',
  PINK_LUG:         'Pink lug',
  // Legacy — should not appear after mig 175 backfill.
  CHEP_KF49_BIN:    'KF49 / CHEP (legacy)',
}

// Tenant-aware "Standard" container list. Post per-tenant bin_types
// (mig 20260701235600) each tenant mirrors only the container codes it actually
// holds an is_system_default row for — so a non-KF49 tenant never sees "KF49
// bin", while KF49 still does (from its own mirror). Falls back to the generic
// set (KF49 excluded) when a tenant has no mirrors yet, and — when a currentValue
// is passed (edit forms) — keeps that value present so an existing row can't
// blank out. Do NOT pass currentValue where the field defaults to KF49_BIN and
// you rely on the picker's snap-to-first correction.
export function standardTypesForTenant(
  binTypes: { code: string; is_system_default: boolean }[],
  currentValue?: ContainerType | null,
): { value: ContainerType; label: string }[] {
  const mirrors = new Set(
    binTypes.filter(t => t.is_system_default).map(t => t.code as ContainerType),
  )
  const set = new Set(GENERAL_CONTAINER_ORDER.filter(c => mirrors.has(c)))
  if (currentValue && !BERRY_CRATE_CODES.has(currentValue)) set.add(currentValue)
  const codes = set.size > 0
    ? [...set]
    : GENERAL_CONTAINER_ORDER.filter(c => !OWN_TENANT_CODES.has(c))  // unseeded fallback
  const ordered = GENERAL_CONTAINER_ORDER.filter(c => codes.includes(c))
  const extras  = codes.filter(c => !GENERAL_CONTAINER_ORDER.includes(c))
  return [...ordered, ...extras].map(c => ({ value: c, label: CONTAINER_LABEL[c] }))
}

// The berry crate a fruit arrives in, from the intake flow's commodity name
// (manifest.commodityName). Null when the name is neither, so the caller keeps
// its free crate-type select for an unknown fruit.
export function crateTypeForCommodity(name: string | null | undefined): ContainerType | null {
  const n = (name ?? '').toUpperCase()
  if (n.includes('STRAWBERR')) return 'STRAWBERRY_CRATE'
  if (n.includes('BLUEBERR')) return 'BLUEBERRY_CRATE'
  return null
}
